import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import RouteIcon from "@mui/icons-material/Route";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChargeEventRow } from "../models/chargeEventRow";
import { FuelUpEventRow } from "../models/fuelUpEventRow";
import { decimalFormatter } from "../utilities/decimalFormatter";
import { formatLocation } from "./useEventAddresses";
import { formatDateTime } from "@/utilities/formatDateTime";

/** What both event views need to turn a row into readable cells. */
export interface EventColumnLookups {
  /** Make and model per vehicle, from the decoded VIN. */
  vinDecodedByDeviceId: Map<string, string>;
  /** Driver display names by id — the entities reference a driver without naming it. */
  driverNames: Map<string, string>;
  /** Addresses by position, from `useEventAddresses`. */
  addresses: Map<string, string>;
}

/** The columns both event views share, in the order they appear. */
function vehicleColumns<TRow extends { deviceId: string }>(
  t: ReturnType<typeof useTranslation>["t"],
  vinDecodedByDeviceId: Map<string, string>,
): GridColDef<TRow>[] {
  return [
    {
      field: "name",
      headerName: t("energyReport.columns.asset", "Fahrzeug"),
      minWidth: 200,
      flex: 1.2,
    },
    {
      field: "licensePlate",
      headerName: t("energyReport.columns.licensePlate", "Kennzeichen"),
      minWidth: 130,
    },
    {
      field: "vinDecoded",
      headerName: t("energyReport.columns.vinDecoded", "Fahrzeugtyp (VIN)"),
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueGetter: (_value, row) => vinDecodedByDeviceId.get(row.deviceId) ?? "",
    },
  ];
}

/** The driver column, resolving the id the entity carries to a name. */
function driverColumn<TRow extends { driverId: string | null }>(
  t: ReturnType<typeof useTranslation>["t"],
  driverNames: Map<string, string>,
): GridColDef<TRow> {
  return {
    field: "driverId",
    headerName: t("energyReport.columns.driver", "Fahrer"),
    minWidth: 200,
    valueGetter: (_value, row) => (row.driverId ? (driverNames.get(row.driverId) ?? "") : ""),
  };
}

/**
 * The columns of the charge events view.
 *
 * Memoised: DataGridPro rebuilds its whole column state on a new array identity, and this page
 * re-renders once per arriving vehicle.
 */
export function useChargeEventColumns({
  vinDecodedByDeviceId,
  driverNames,
  addresses,
}: EventColumnLookups): GridColDef<ChargeEventRow>[] {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  return useMemo<GridColDef<ChargeEventRow>[]>(() => {
    const decimals = decimalFormatter(language);

    return [
      ...vehicleColumns<ChargeEventRow>(t, vinDecodedByDeviceId),
      {
        field: "startTime",
        headerName: t("energyReport.columns.chargeStart", "Ladebeginn"),
        minWidth: 170,
        valueFormatter: value => formatDateTime(value as Date | undefined, language),
      },
      {
        field: "durationHours",
        headerName: t("energyReport.columns.durationHours", "Dauer (h)"),
        type: "number",
        minWidth: 120,
        valueFormatter: value => decimals(value as number | null),
      },
      {
        field: "startStateOfCharge",
        headerName: t("energyReport.columns.startSoc", "Ladestand Start (%)"),
        type: "number",
        minWidth: 160,
        valueFormatter: value => decimals(value as number | null, 0),
      },
      {
        field: "endStateOfCharge",
        headerName: t("energyReport.columns.endSoc", "Ladestand Ende (%)"),
        type: "number",
        minWidth: 160,
        valueFormatter: value => decimals(value as number | null, 0),
      },
      {
        field: "energyConsumedKwh",
        headerName: t("energyReport.columns.energy", "Energie (kWh)"),
        type: "number",
        minWidth: 150,
        valueFormatter: value => decimals(value as number | null),
        renderCell: ({ row, formattedValue }) => (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            <span>{formattedValue}</span>
            {row.energyIsEstimated && (
              <Tooltip
                title={t(
                  "energyReport.chargeEstimatedHint",
                  "Geotab hat diesen Wert geschätzt, weil das Fahrzeug kein direktes Messsignal liefert.",
                )}
              >
                <BatteryChargingFullIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "distanceSinceLastChargeKm",
        headerName: t(
          "energyReport.columns.distanceSinceCharge",
          "Strecke seit letzter Ladung (km)",
        ),
        type: "number",
        minWidth: 220,
        valueFormatter: value => decimals(value as number | null, 0),
        renderCell: ({ row, formattedValue }) => (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            <span>{formattedValue}</span>
            {row.distanceIsDerived && (
              <Tooltip
                title={t(
                  "energyReport.derivedDistanceHint",
                  "Aus der Differenz der Kilometerstände zweier Ladevorgänge errechnet, weil Geotab keinen Wert geliefert hat.",
                )}
              >
                <RouteIcon fontSize="small" color="disabled" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "odometerKm",
        headerName: t("energyReport.columns.odometer", "Kilometerstand (km)"),
        type: "number",
        minWidth: 170,
        valueFormatter: value => decimals(value as number | null, 0),
      },
      driverColumn<ChargeEventRow>(t, driverNames),
      {
        field: "location",
        headerName: t("energyReport.columns.location", "Ort"),
        minWidth: 220,
        flex: 1,
        sortable: false,
        valueGetter: (_value, row) => formatLocation(row.location, addresses),
      },
    ];
  }, [t, language, vinDecodedByDeviceId, driverNames, addresses]);
}

/**
 * The columns of the fill-up events view.
 *
 * Memoised for the same reason as the charge event columns.
 */
export function useFuelUpEventColumns({
  vinDecodedByDeviceId,
  driverNames,
  addresses,
}: EventColumnLookups): GridColDef<FuelUpEventRow>[] {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  return useMemo<GridColDef<FuelUpEventRow>[]>(() => {
    const decimals = decimalFormatter(language);

    return [
      ...vehicleColumns<FuelUpEventRow>(t, vinDecodedByDeviceId),
      {
        field: "dateTime",
        headerName: t("energyReport.columns.fuelUpTime", "Zeitpunkt"),
        minWidth: 170,
        valueFormatter: value => formatDateTime(value as Date | undefined, language),
      },
      {
        field: "volumeLiters",
        headerName: t("energyReport.columns.derivedVolume", "Menge aus Tankfüllstand (l)"),
        type: "number",
        minWidth: 200,
        valueFormatter: value => decimals(value as number | null),
      },
      {
        field: "cardVolumeLiters",
        headerName: t("energyReport.columns.cardVolume", "Menge laut Tankkarte (l)"),
        type: "number",
        minWidth: 190,
        valueFormatter: value => decimals(value as number | null),
      },
      {
        field: "totalFuelUsedLiters",
        headerName: t("energyReport.columns.totalFuelUsed", "Zählerstand Verbrauch (l)"),
        type: "number",
        minWidth: 190,
        valueFormatter: value => decimals(value as number | null),
      },
      {
        field: "deviationFactor",
        headerName: t("energyReport.columns.deviationFactor", "Abweichungsfaktor"),
        description: t(
          "energyReport.columns.deviationFactorHint",
          "Von Geotab ermittelte Abweichung zwischen zugeführter und verbrauchter Menge.",
        ),
        type: "number",
        minWidth: 160,
        valueFormatter: value => decimals(value as number | null),
      },
      {
        field: "odometerKm",
        headerName: t("energyReport.columns.odometer", "Kilometerstand (km)"),
        type: "number",
        minWidth: 170,
        valueFormatter: value => decimals(value as number | null, 0),
      },
      driverColumn<FuelUpEventRow>(t, driverNames),
      {
        field: "hasTransaction",
        headerName: t("energyReport.columns.hasTransaction", "Tankkartenbeleg"),
        minWidth: 160,
        type: "boolean",
      },
      {
        field: "location",
        headerName: t("energyReport.columns.location", "Ort"),
        minWidth: 220,
        flex: 1,
        sortable: false,
        valueGetter: (_value, row) => formatLocation(row.location, addresses),
      },
    ];
  }, [t, language, vinDecodedByDeviceId, driverNames, addresses]);
}
